import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

const PRODUCT_CARD_LAYOUT_CHOICES = [
  {
    value: 1,
    label: "C1",
    image: {
      link: "assets/img/product-card/card-1.png",
      alt: "Card 1",
      width: "82px",
      height: "100px",
    },
  },
  {
    value: 2,
    label: "C2",
    image: {
      link: "assets/img/product-card/card-2.png",
      alt: "Card 2",
      width: "82px",
      height: "100px",
    },
  },
  {
    value: 3,
    label: "C3",
    image: {
      link: "assets/img/product-card/card-3.png",
      alt: "Card 3",
      width: "82px",
      height: "100px",
    },
  },
  {
    value: 5,
    label: "C5",
    image: {
      link: "assets/img/product-card/card-5.png",
      alt: "Card 5",
      width: "82px",
      height: "100px",
    },
  },
  {
    value: 4,
    label: "C4",
    image: {
      link: "assets/img/product-card/card-4.png",
      alt: "Card 4",
      width: "168px",
      height: "80px",
    },
  },
  {
    value: 6,
    label: "C6",
    image: {
      link: "assets/img/product-card/card-6.png",
      alt: "Card 6",
      width: "168px",
      height: "80px",
    },
  },
] as const;

export const KIOSKMAINLAYOUT_LEGACY_FIELD_ORDER = [
  {
    type: "groupedFields",
    handle: "kiosk_global_settings",
    containerType: "default",
    groups: [
      {
        label:
          "pages.blocks.kiosk-main-layout.fieldset.product-composition.label",
        containerType: "fieldset",
        fields: ["stepperProductComposition", "productCard"],
      },
      {
        label: "pages.blocks.kiosk-main-layout.shop-closed-modal.label",
        containerType: "fieldset",
        fields: ["shop_closed_modal"],
      },
      {
        label: "pages.blocks.kiosk-main-layout.fieldset.multi-currency.label",
        containerType: "fieldset",
        fields: ["multiCurrency"],
      },
    ],
  },
  {
    type: "heading",
    value: "pages.blocks.kiosk-main-layout.fieldset.blocks.label",
    level: 3,
  },
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  kioskMainLayoutDescriptor: _generatedKioskMainLayoutBlockDescriptor,
  kioskMainLayoutBlocDefinition,
  kioskMainLayoutDashboardLayout,
  kioskMainLayoutConfig,
  kioskMainLayoutResolveConfig,
  kioskMainLayoutProperties,
} = doodBlockConfig(
  {
    name: "Kiosk Main layout",
    componentType: "KioskMainLayoutComponent",
    category: "layout",
    description: "Kiosk Main layout.",
    keywords: [],
    isRootBlock: true,
    sharedFields: {},
    slots: [
      {
        key: "footer",
        name: "pages.blocks.kiosk-main-layout.footer.label",
        accept: "explicitOnly",
        allowedTypes: ["BigFooterBlock", "LegalsFooterBlock", "HtmlBlock"],
        slotItemFormat: "ids",
      },
      {
        key: "global_features",
        name: "pages.blocks.kiosk-main-layout.global-features.label",
        accept: "explicitOnly",
        allowedTypes: [".*(?<!Page)"],
        slotItemFormat: "ids",
        allowedRoles: ["ADMIN"],
      },
    ],
  },

  () => ({
    kiosk_global_settings: {
      stepperProductComposition: {
        autoAddToCart: propertyValue({
          type: "checkbox",
          value: false,
          options: {
            label: "pages.blocks.kiosk-main-layout.auto-add-to-cart.label",
            help: "pages.blocks.kiosk-main-layout.auto-add-to-cart.help",
          },
        }),
        hideNextButtonIfTheRequiredQuantityIsOne: propertyValue({
          type: "checkbox",
          value: false,
          options: {
            label:
              "pages.blocks.kiosk-main-layout.hide-next-button-if-required-quantity-is-one.label",
            help: "pages.blocks.kiosk-main-layout.hide-next-button-if-required-quantity-is-one.help",
          },
        }),
      },
      productCard: {
        choice: propertyValue({
          type: "radio",
          value: 1,
          options: {
            label: "pages.blocks.kiosk-main-layout.product-card.label",
            choices: [...PRODUCT_CARD_LAYOUT_CHOICES],
          },
        }),
        styles: {
          type: propertyValue({
            type: "select",
            value: "defaultCard",
            options: {
              multiple: false,
              label:
                "pages.blocks.kiosk-main-layout.product-card-styles-type.label",
              choices: [
                {
                  label:
                    "pages.blocks.kiosk-main-layout.product-card-styles-type.options.defaultCard",
                  value: "defaultCard",
                },
                {
                  label:
                    "pages.blocks.kiosk-main-layout.product-card-styles-type.options.shadowCard",
                  value: "shadowCard",
                },
                {
                  label:
                    "pages.blocks.kiosk-main-layout.product-card-styles-type.options.borderedCard",
                  value: "borderedCard",
                },
                {
                  label:
                    "pages.blocks.kiosk-main-layout.product-card-styles-type.options.roundedCard",
                  value: "roundedCard",
                },
              ],
            },
          }),
        },
      },
      shop_closed_modal: {
        title: propertyValue({
          type: "text",
          value: "",
          options: {
            label:
              "pages.blocks.kiosk-main-layout.shop-closed-modal-title.label",
          },
        }),
        message: propertyValue({
          type: "textarea",
          value: "",
          options: {
            label:
              "pages.blocks.kiosk-main-layout.shop-closed-modal-message.label",
          },
        }),
      },
      multiCurrency: {
        mode: propertyValue({
          type: "select",
          value: "off",
          options: {
            multiple: false,
            label: "pages.blocks.kiosk-main-layout.multi-currency.label",
            help: "pages.blocks.kiosk-main-layout.multi-currency.help",
            choices: [
              {
                label:
                  "pages.blocks.kiosk-main-layout.multi-currency.options.off",
                value: "off",
              },
              {
                label:
                  "pages.blocks.kiosk-main-layout.multi-currency.options.sco",
                value: "sco",
              },
              {
                label:
                  "pages.blocks.kiosk-main-layout.multi-currency.options.test",
                value: "test",
              },
            ],
          },
        }),
      },
    },
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.kiosk-main-layout.tab.content.label",
        fieldsets: [
          {
            legend:
              "pages.blocks.kiosk-main-layout.fieldset.product-composition.label",
            fields: [
              "kiosk_global_settings.stepperProductComposition.autoAddToCart",
              "kiosk_global_settings.stepperProductComposition.hideNextButtonIfTheRequiredQuantityIsOne",
            ],
          },
          {
            legend:
              "pages.blocks.kiosk-main-layout.fieldset.product-card.label",
            fields: [
              "kiosk_global_settings.productCard.choice",
              "kiosk_global_settings.productCard.styles.type",
            ],
          },
          {
            legend:
              "pages.blocks.kiosk-main-layout.fieldset.shop-closed-modal.label",
            fields: [
              "kiosk_global_settings.shop_closed_modal.title",
              "kiosk_global_settings.shop_closed_modal.message",
            ],
          },
          {
            legend:
              "pages.blocks.kiosk-main-layout.fieldset.multi-currency.label",
            fields: ["kiosk_global_settings.multiCurrency.mode"],
          },
          {
            legend: "pages.blocks.kiosk-main-layout.fieldset.blocks.label",
            fields: ["footer", "global_features"],
          },
        ],
      },
    ],
  }),
);

export function kioskMainLayoutDescriptor(): IBlockDescriptor;
export function kioskMainLayoutDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function kioskMainLayoutDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedKioskMainLayoutBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedKioskMainLayoutBlockDescriptor,
    kioskMainLayoutProperties,
    fieldOrder,
  );
}

export {
  kioskMainLayoutBlocDefinition,
  kioskMainLayoutDashboardLayout,
  kioskMainLayoutProperties,
  kioskMainLayoutConfig,
  kioskMainLayoutResolveConfig,
};

export type KioskMainLayoutConfig = typeof kioskMainLayoutConfig;
